import { useEffect } from 'react';
/**
 * Why this hook exists
 *
 * Toolpad's built-in sidebar unmounts descendant items when a group is collapsed.
 * That means we cannot rely on descendant DOM (or CSS :has selectors) to visually
 * mark the parent as "active" while collapsed, even though the current route is
 * inside that group.
 *
 * What the hook does
 * - Derives the active leaf from the current pathname using our modules navigation
 *   helpers (strip client prefix + longest prefix match).
 * - Computes the ancestor group labels for that leaf from precomputed navigation data.
 * - Applies small, deterministic CSS classes on the parent ListItemButton(s):
 *   - `encore-parent-active`       → this parent is an ancestor of the active leaf
 *   - `encore-parent-expanded`     → the parent is currently expanded (open)
 *   - `encore-parent-collapsed`    → the parent is currently collapsed (closed)
 *
 * Styling model
 * - Theme rules style `.encore-parent-active.encore-parent-collapsed` with the
 *   selected background (so collapsed parents look active without children mounted).
 * - Expanded parents keep only primary text/icon color, not the selected background.
 *
 * Why not a pure React approach?
 * - Toolpad does not expose a render override for each nav node. Short of replacing
 *   the entire sidebar with a custom React implementation, the light-touch class
 *   toggle is the minimal, reliable way to express the desired visuals.
 */
import {
  findMatchingLeaf,
  stripClientPrefix,
  type ModuleLeafRoute,
} from '../utils/modulesNavigation';

type UseActiveNavGroupsOptions = {
  pathname: string;
  leaves: ModuleLeafRoute[];
  navigationSignature: string;
};

export function useActiveNavGroups({
  pathname,
  leaves,
  navigationSignature,
}: UseActiveNavGroupsOptions) {
  useEffect(() => {
    const apply = () => {
      const clientless = stripClientPrefix(pathname);
      const match = findMatchingLeaf(clientless, leaves);
      const ancestorLabels = match?.ancestorLabels ?? [];

      const buttons = Array.from(
        document.querySelectorAll('.MuiListItem-root > .MuiListItemButton-root')
      ) as HTMLElement[];

      for (const btn of buttons) {
        const label = (btn.textContent ?? '').trim().replace(/\s+/g, ' ');
        const listItem = btn.closest('.MuiListItem-root') as HTMLElement | null;
        const nextSibling = listItem?.nextElementSibling as HTMLElement | null;
        const collapseEl = nextSibling?.matches('.MuiCollapse-root')
          ? nextSibling
          : null;
        const ariaExpanded = btn.getAttribute('aria-expanded');
        const isExpandedAria = ariaExpanded === 'true';
        const isExpandedCollapse = !!collapseEl && collapseEl.clientHeight > 0;
        const isExpanded = isExpandedAria || isExpandedCollapse;

        if (label && ancestorLabels.includes(label)) {
          btn.classList.add('encore-parent-active');
          if (isExpanded) {
            btn.classList.add('encore-parent-expanded');
            btn.classList.remove('encore-parent-collapsed');
          } else {
            btn.classList.add('encore-parent-collapsed');
            btn.classList.remove('encore-parent-expanded');
          }
        } else {
          btn.classList.remove('encore-parent-active');
          btn.classList.remove('encore-parent-expanded');
          btn.classList.remove('encore-parent-collapsed');
        }
      }
    };

    // Apply after current paint to ensure DOM is ready
    const raf = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(raf);
  }, [pathname, leaves, navigationSignature]);
}
